import { ReactNode, useCallback, useState } from 'react';

import { Typography } from '@mui/material';
import { DialogProps } from '@mui/material/Dialog/Dialog';
import { deleteRecord } from 'config/requests';
import { RaRecord, useNotify, useRefresh } from 'react-admin';
import { CancelButton, DeleteButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';

export type DeleteDialogProps = {
  alert?: JSX.Element;
  record?: RaRecord;
  title: string | ReactNode;
  details?: string | ReactNode;
  onClose: () => void;
  onSuccess?: () => void;
  onDelete?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  width?: number;
} & DialogProps;

export const DeleteDialog: React.FC<DeleteDialogProps> = ({
  alert,
  open,
  onClose,
  onCancel,
  onDelete,
  record,
  title,
  details,
  onSuccess,
  isLoading,
}) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [isLoadingData, setIsLoadingData] = useState(false);

  const deleteOneRecord = useCallback(() => {
    if (onDelete) {
      onDelete();

      return;
    }
    setIsLoadingData(true);
    deleteRecord(record?.id.toString() || '')
      .then(() => {
        refresh();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((error: any) => {
        const errorDetail = error?.data?.detail;
        if (errorDetail) {
          notify(errorDetail, { type: 'error', multiLine: true });
          return;
        }

        notify('Неизвестная ошибка', { type: 'error', multiLine: true });
      })
      .finally(() => setIsLoadingData(false));
  }, [record, onDelete]);

  return (
    <Dialog onClose={onClose} open={open} sx={{ '& .MuiDialog-paper': { minWidth: '640px' } }}>
      <CardHeader
        appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
        title={title}
        titleSize="smallBold"
      />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {alert && alert}
        {details && <Typography variant="body1">{details}</Typography>}
      </CardContent>
      <CardActionsButtons
        rightActionsSlot={[
          <CancelButton key="cancel-button" onClick={onCancel || onClose} variant="outlined" />,
          <DeleteButton
            disabled={isLoading || isLoadingData}
            isLoading={isLoading || isLoadingData}
            key="delete-base-button"
            onClick={deleteOneRecord}
          />,
        ]}
      />
    </Dialog>
  );
};
